import { useRef, useEffect, useMemo, useState } from "react";
import {
  Popper,
  Paper,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
} from "@mui/material";
import ATextField from "../../../../components/UI/Inputs/MUI/ATextField/ATextField";
import "./EmailStep.module.scss";

// Lista de provedores de e-mail para sugestões
const EMAIL_PROVIDERS = [
  "gmail.com",
  "hotmail.com",
  "outlook.com",
  "yahoo.com",
];

// Validação de e-mail regex
const EMAIL_REGEX = /^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$/;

/**
 * Componente EmailStep - Gerencia o campo de entrada de e-mail com sugestões automáticas
 * @param {string} email - O valor atual do e-mail
 * @param {string} errorMsg - Mensagem de erro para exibir, se houver
 * @param {boolean} isLoading - Indica se o componente está em estado de carregamento
 * @param {function} onChange - Função para lidar com mudanças no campo de e-mail
 */
const EmailStep = ({ email, errorMsg, isLoading, onChange }) => {
  const emailInputRef = useRef(null);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const popperRef = useRef(null);

  // Foco no campo de e-mail quando o componente é montado
  useEffect(() => {
    if (emailInputRef.current) {
      emailInputRef.current.focus();
    }
  }, []);

  // Gerar sugestões de e-mail mais inteligentes
  const emailOptions = useMemo(() => {
    if (!email || email.length < 2) return [];

    // Se já tem @, gerar sugestões baseadas no domínio
    if (email.includes("@")) {
      const [username, domain] = email.split("@");
      if (!username) return [];

      // Se o domínio está incompleto, sugerir baseado no que foi digitado
      if (domain) {
        return EMAIL_PROVIDERS.filter((provider) =>
          provider.toLowerCase().startsWith(domain.toLowerCase())
        )
          .map((provider) => `${username}@${provider}`)
          .filter((suggestion) => suggestion !== email);
      } else {
        // Se só tem @, mostrar todas as opções
        return EMAIL_PROVIDERS.map((provider) => `${username}@${provider}`);
      }
    } else {
      // Se não tem @, sugerir completar com @
      return EMAIL_PROVIDERS.map((provider) => `${email}@${provider}`);
    }
  }, [email]);

  // Verificar se deve mostrar sugestões
  const shouldShowSuggestions = useMemo(() => {
    return email.length >= 2 && emailOptions.length > 0;
  }, [email, emailOptions]);

  // Lidar com alterações no campo de entrada
  const handleEmailChange = (e) => {
    const newValue = e.target.value;
    onChange({
      email: newValue,
      errorMsg: null,
    });
    console.log(newValue);

    // Atualizar anchorEl e mostrar sugestões se apropriado
    setAnchorEl(e.target);
    if (newValue.length >= 2) {
      setShowSuggestions(true);
    } else {
      setShowSuggestions(false);
    }
  };

  // Selecionar uma sugestão
  const handleSuggestionSelect = (suggestion) => {
    onChange({
      email: suggestion,
      errorMsg: null,
    });

    // Manter o foco no input após seleção
    handleEmailChange({ target: { value: suggestion } });
    setTimeout(() => {
      setShowSuggestions(false);
      if (emailInputRef.current) {
        emailInputRef.current.focus();
        // Posicionar cursor no final
        // emailInputRef.current.setSelectionRange(suggestion.length, suggestion.length);
      }
    }, 0);
  };

  // Validar e-mail no desfoque
  const validateEmail = () => {
    if (email && !EMAIL_REGEX.test(email)) {
      onChange({ errorMsg: "Por favor, insira um e-mail válido" });
    }
    setTimeout(() => {
      // se o foco atual não está dentro do popper, esconde
      if (
        popperRef.current &&
        !popperRef.current.contains(document.activeElement)
      ) {
        setShowSuggestions(false);
      }
    }, 0);
  };

  // Mostrar sugestões no foco
  const handleFocus = (e) => {
    setAnchorEl(e.target);
    const currentValue = emailInputRef.current?.value || ""; // <-- LÊ O VALOR ATUAL
    
    // Mostra se o input atual tem 2 ou mais caracteres E há opções
    if (currentValue.length >= 2 && emailOptions.length > 0) { 
      setShowSuggestions(true);
    }
  };

  // Mostrar sugestões no clique
  const handleClick = (e) => {
    setAnchorEl(e.target);
    const currentValue = emailInputRef.current?.value || ""; // <-- LÊ O VALOR ATUAL
    
    // Mostra se o input atual tem 2 ou mais caracteres E há opções
    if (currentValue.length >= 2 && emailOptions.length > 0) {
      setShowSuggestions(true);
    }
  };

  // Lidar com teclas
  const handleKeyDown = (e) => {
    if (e.key === "Escape") {
      setShowSuggestions(false);
    } else if (
      e.key === "ArrowDown" &&
      showSuggestions &&
      emailOptions.length > 0
    ) {
      // Opcional: navegar pelas sugestões com seta
      e.preventDefault();
    }
  };

  return (
    <div className="email-step">
      <ATextField
        inputRef={emailInputRef}
        label="Digite seu e-mail"
        variant="outlined"
        sx={{
          backgroundColor: "#F2F2F2",
          "& .MuiInputLabel-root": {
            fontWeight: "500",
            fontSize: "1.1rem",
          },
          "& .MuiInputBase-input": {
            fontWeight: "500",
          },
        }}
        required
        type="email"
        value={email}
        error={!!errorMsg}
        helperText={errorMsg}
        onChange={handleEmailChange}
        onBlur={validateEmail}
        onFocus={handleFocus}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        disabled={isLoading}
        className="email-input"
        name="email"
        id="email"
        autoComplete="username email"
        inputProps={{
          autoComplete: "username email",
          name: "email",
          id: "email",
        }}
        fullWidth
      />

      {/* Sugestões de e-mail */}
      <Popper
        open={showSuggestions && emailOptions.length > 0}
        anchorEl={anchorEl}
        ref={popperRef}
        placement="bottom-start"
        style={{
          zIndex: 1300,
          width: anchorEl?.offsetWidth,
          maxWidth: 400,
        }}
        modifiers={[
          {
            name: "offset",
            options: {
              offset: [0, 4],
            },
          },
        ]}
      >
        <Paper elevation={3} sx={{ maxHeight: 200, overflow: "auto" }}>
          <List dense>
            {emailOptions.slice(0, 4).map((suggestion, index) => (
              <ListItem key={index} disablePadding>
                <ListItemButton
                  onClick={() => handleSuggestionSelect(suggestion)}
                  sx={{
                    py: 0.75,
                    "&:hover": {
                      backgroundColor: "action.hover",
                    },
                  }}
                >
                  <ListItemText
                    primary={suggestion}
                    sx={{
                      "& .MuiListItemText-primary": {
                        fontSize: "0.9rem",
                      },
                    }}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Paper>
      </Popper>
    </div>
  );
};

export default EmailStep;
